/**
 * Free Medical Treatment Type Edit Modal
 *
 * Modal for creating/editing a free medical treatment type.
 * Validation with Zod schema (aligned with backend FreeMedicalTreatmentTypeStoreRequest / UpdateRequest).
 */

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  TreatmentTypeFormData,
  FreeMedicalTreatmentType,
  FreeMedicalInitData,
  FreeMedicalServiceType,
  FreeMedicalTreatmentCategory,
  ProductSaleType,
} from '@/pages/main/freeMedical/types';
import { freeMedicalTreatmentTypeSchema } from '@/schemas/freeMedicalTreatmentTypeSchema';
import useAppToast from '@/hooks/useAppToast';
import { useFormChangeGuard } from '@/hooks/useFormChangeGuard';
import {
  AppTextField,
  AppSelect,
  AppCheckbox,
  AppInputNumber,
  AppButton,
  AppModal,
  AppRow,
  AppCol,
  AppFormLabel,
} from '@/components/atoms';

const PRICE_MAX = 9999999999;

const REQUIRED_TIME_OPTIONS = [
  0, 10, 15, 20, 25, 30, 35, 40, 45, 50, 60, 70, 75, 80, 90, 100, 105, 110, 120,
].map(min => ({
  label: `${min}分`,
  value: min,
}));

/** Category with 物販 classification: duration (所要時間) is not used. */
function categoryIsMerchandise(
  category: FreeMedicalTreatmentCategory | undefined,
  productSaleTypes: ProductSaleType[] | undefined
): boolean {
  if (!category) return false;
  if (category.product_sale_type?.type_name === '物販') return true;
  const id = category.product_sale_type_id;
  if (id == null) return false;
  const t = productSaleTypes?.find(p => p.id === id);
  return t?.type_name === '物販';
}

interface FreeMedicalTreatmentTypeEditModalProps {
  isOpen: boolean;
  treatmentType: FreeMedicalTreatmentType | null;
  isNew: boolean;
  initData: FreeMedicalInitData | null;
  onClose: () => void;
  onSave: (data: TreatmentTypeFormData) => Promise<void>;
}

type TreatmentTypeFormStateDraft = Omit<
  TreatmentTypeFormData,
  'free_medical_treatment_category_id' | 'price' | 'service_type'
> & {
  free_medical_treatment_category_id?: number;
  service_type?: FreeMedicalServiceType;
  price?: number;
  sort_number?: number;
};

const FreeMedicalTreatmentTypeEditModal: React.FC<FreeMedicalTreatmentTypeEditModalProps> = ({
  isOpen,
  treatmentType,
  isNew,
  initData,
  onClose,
  onSave,
}) => {
  const toast = useAppToast();
  const [formData, setFormData] = useState<TreatmentTypeFormStateDraft>({
    free_medical_treatment_category_id: undefined,
    service_type: undefined,
    name: '',
    price: 0,
    required_time: null,
    default_flag: false,
    sort_number: undefined,
  });
  const [initialFormData, setInitialFormData] = useState<TreatmentTypeFormStateDraft>({
    free_medical_treatment_category_id: undefined,
    service_type: undefined,
    name: '',
    price: 0,
    required_time: null,
    default_flag: false,
    sort_number: undefined,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const selectedCategory = useMemo(() => {
    const id = formData.free_medical_treatment_category_id;
    if (id == null || !initData?.categories?.length) return undefined;
    return initData.categories.find(c => c.id === id);
  }, [formData.free_medical_treatment_category_id, initData?.categories]);

  const serviceTypeOptions = useMemo(
    () => initData?.service_types ?? [],
    [initData?.service_types]
  );

  const isMerchandiseCategory = useMemo(
    () => categoryIsMerchandise(selectedCategory, initData?.product_sale_types),
    [selectedCategory, initData?.product_sale_types]
  );

  /**
   * Validate form using Zod schema; updates errors state and returns parsed data when valid
   */
  const validateForm = useCallback(() => {
    const validation = freeMedicalTreatmentTypeSchema.safeParse(formData);

    if (!validation.success) {
      const fieldErrors: Record<string, string> = {};
      const flat = validation.error.flatten().fieldErrors;
      Object.entries(flat).forEach(([key, arr]) => {
        if (arr && arr.length) {
          fieldErrors[key] = arr[0] as string;
        }
      });
      setErrors(fieldErrors);
      return null;
    }
    setErrors({});
    return validation.data;
  }, [formData]);

  useEffect(() => {
    if (isOpen) {
      if (treatmentType && !isNew) {
        const cat = initData?.categories?.find(
          c => c.id === treatmentType.free_medical_treatment_category_id
        );
        const merchandise = categoryIsMerchandise(cat, initData?.product_sale_types);
        const newFormData = {
          free_medical_treatment_category_id: treatmentType.free_medical_treatment_category_id,
          service_type:
            serviceTypeOptions.length === 1
              ? serviceTypeOptions[0].value
              : (treatmentType.service_type ?? undefined),
          name: treatmentType.name || '',
          price: treatmentType.price ?? undefined,
          required_time: merchandise ? null : (treatmentType.required_time ?? null),
          default_flag: treatmentType.default_flag || false,
          sort_number: treatmentType.sort_number ?? undefined,
        };
        setFormData(newFormData);
        setInitialFormData(newFormData);
      } else {
        const newFormData = {
          free_medical_treatment_category_id: undefined,
          service_type: initData?.default_service_type ?? undefined,
          name: '',
          price: 0,
          required_time: null,
          default_flag: false,
          sort_number: (initData?.categories?.length || 0) + 1,
        };
        setFormData(newFormData);
        setInitialFormData(newFormData);
      }
      setErrors({});
    } else {
      const defaultData = {
        free_medical_treatment_category_id: undefined,
        service_type: undefined,
        name: '',
        price: 0,
        required_time: null,
        default_flag: false,
        sort_number: undefined as number | undefined,
      };
      setFormData(defaultData);
      setInitialFormData(defaultData);
    }
  }, [isOpen, treatmentType, isNew, initData, serviceTypeOptions]);

  const handleDefaultFlagChange = (checked: boolean) => {
    setFormData({
      ...formData,
      default_flag: checked,
      price: checked ? 0 : formData.price,
    });
  };

  /**
   * Detect if form has changed using unified hook
   */
  const { confirmIfChanged, clearFormChange } = useFormChangeGuard(formData, initialFormData);

  const handleClose = () => {
    confirmIfChanged(() => {
      clearFormChange();
      onClose();
    });
  };

  const handleSubmit = async () => {
    const parsed = validateForm();
    if (!parsed) {
      return;
    }

    setSaving(true);
    setErrors({});

    try {
      const categoryId = parsed.free_medical_treatment_category_id;
      const cat = initData?.categories?.find(c => c.id === categoryId);
      const merchandise = categoryIsMerchandise(cat, initData?.product_sale_types);
      const payload: TreatmentTypeFormData = {
        free_medical_treatment_category_id: categoryId,
        service_type: parsed.service_type,
        name: parsed.name,
        price: parsed.price,
        required_time: merchandise ? null : (parsed.required_time ?? null),
        default_flag: parsed.default_flag,
      };
      await onSave(payload);
      clearFormChange();
      onClose();
    } catch (err: unknown) {
      if (err && typeof err === 'object') {
        const fallback = 'データが見つかりません。';
        if ('errors' in err) {
          const errorData = err as { errors: Record<string, string | string[]> };
          setErrors(
            Object.fromEntries(
              Object.entries(errorData.errors).map(([k, v]) => [
                k,
                Array.isArray(v) ? v[0] : String(v),
              ])
            ) as Record<string, string>
          );
          const first = Object.values(errorData.errors)[0];
          toast.error(Array.isArray(first) ? first[0] : String(first ?? fallback));
        } else if ('message' in err) {
          const errorMsg = err as { message: string };
          toast.error(errorMsg.message || fallback);
        }
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppModal
      isOpen={isOpen}
      onClose={handleClose}
      maxWidth="max-w-lg"
      title={<span>{isNew ? '自由診療メニュー登録' : '自由診療メニュー編集'}</span>}
      footer={[
        <AppButton key="save" onClick={handleSubmit} disabled={saving} type="primary">
          {saving ? '保存中...' : '保存'}
        </AppButton>,
      ]}
    >
      <AppRow gutter={[16, 16]}>
        <AppCol span={24}>
          <AppRow gutter={16} align="middle">
            <AppCol span={8}>
              <AppFormLabel label="カテゴリ名" required htmlFor="type_category" />
            </AppCol>
            <AppCol span={16}>
              <AppSelect
                id="type_category"
                value={formData.free_medical_treatment_category_id || undefined}
                onChange={value => {
                  const nextId = value === null || value === undefined ? undefined : Number(value);
                  const nextCat = initData?.categories?.find(c => c.id === nextId);
                  const nextMerchandise = categoryIsMerchandise(
                    nextCat,
                    initData?.product_sale_types
                  );
                  setFormData({
                    ...formData,
                    free_medical_treatment_category_id: nextId,
                    required_time: nextMerchandise ? null : formData.required_time,
                  });
                }}
                className={
                  errors.free_medical_treatment_category_id ? 'border-red-500 w-full' : 'w-full'
                }
                options={(initData?.categories || []).map(cat => ({
                  label: cat.category_name,
                  value: cat.id,
                }))}
              />
              {errors.free_medical_treatment_category_id ? (
                <div className="mt-1 text-xs text-red-500">
                  {errors.free_medical_treatment_category_id}
                </div>
              ) : null}
            </AppCol>
          </AppRow>
        </AppCol>

        <AppCol span={24}>
          <AppRow gutter={16} align="middle">
            <AppCol span={8}>
              <AppFormLabel label="サービス" required htmlFor="type_service" />
            </AppCol>
            <AppCol span={16}>
              <AppSelect
                id="type_service"
                value={formData.service_type}
                onChange={value =>
                  setFormData({
                    ...formData,
                    service_type: (value ?? undefined) as FreeMedicalServiceType | undefined,
                  })
                }
                // A single-service clinic has no choice to make
                disabled={serviceTypeOptions.length <= 1}
                className={errors.service_type ? 'border-red-500 w-full' : 'w-full'}
                options={serviceTypeOptions}
              />
              {errors.service_type ? (
                <div className="mt-1 text-xs text-red-500">{errors.service_type}</div>
              ) : null}
            </AppCol>
          </AppRow>
        </AppCol>

        <AppCol span={24}>
          <AppRow gutter={16} align="middle">
            <AppCol span={8}>
              <AppFormLabel label="自由診療名" required htmlFor="type_name" />
            </AppCol>
            <AppCol span={16}>
              <AppTextField
                id="type_name"
                value={formData.name}
                onChange={e => setFormData({ ...formData, name: e.target.value })}
                placeholder="自由診療名を入力してください"
                className={errors.name ? 'border-red-500' : ''}
                maxLength={255}
              />
              {errors.name ? <div className="mt-1 text-xs text-red-500">{errors.name}</div> : null}
            </AppCol>
          </AppRow>
        </AppCol>

        <AppCol span={24}>
          <AppRow gutter={16} align="middle">
            <AppCol span={8}>
              <AppFormLabel label="金額（税込）" required htmlFor="type_price" />
            </AppCol>
            <AppCol span={16}>
              <div className="flex items-center gap-2">
                <AppInputNumber
                  id="type_price"
                  value={Number.isFinite(formData.price) ? formData.price : undefined}
                  onChange={val =>
                    setFormData({
                      ...formData,
                      price:
                        val === null || val === undefined
                          ? undefined
                          : Math.min(Math.max(0, Number(val)), PRICE_MAX),
                    })
                  }
                  onBlur={() => {
                    setFormData(prev => {
                      if (prev.price === undefined || prev.price === null) {
                        return { ...prev, price: 0 };
                      }
                      return prev;
                    });
                  }}
                  className={errors.price ? 'border-red-500 w-1/2' : 'w-1/2'}
                  min={0}
                  max={PRICE_MAX}
                  disabled={formData.default_flag}
                  parser={raw => {
                    const digits = String(raw ?? '').replace(/[^\d]/g, '');
                    if (!digits) return '';
                    const n = Number(digits);
                    if (!Number.isFinite(n)) return '';
                    return Math.min(n, PRICE_MAX);
                  }}
                  formatter={value => (value === undefined || value === null ? '' : String(value))}
                />
                <span className="text-sm">円</span>
              </div>
              {errors.price ? (
                <div className="mt-1 text-xs text-red-500">{errors.price}</div>
              ) : null}
            </AppCol>
          </AppRow>
        </AppCol>

        <AppCol span={24}>
          <AppRow gutter={16} align="middle">
            <AppCol span={8}>
              <AppFormLabel label="所要時間" htmlFor="type_required_time" />
            </AppCol>
            <AppCol span={16}>
              <AppSelect
                id="type_required_time"
                value={formData.required_time ?? undefined}
                onChange={value =>
                  setFormData({
                    ...formData,
                    required_time: value === null || value === undefined ? null : Number(value),
                  })
                }
                allowClear
                disabled={isMerchandiseCategory}
                options={REQUIRED_TIME_OPTIONS}
                className={isMerchandiseCategory ? 'opacity-60 w-full' : 'w-full'}
              />
            </AppCol>
          </AppRow>
        </AppCol>

        <AppCol span={24}>
          <AppRow gutter={16} align="middle">
            <AppCol span={8}>
              <AppFormLabel label="常時" />
            </AppCol>
            <AppCol span={16}>
              <AppCheckbox
                checked={formData.default_flag}
                onChange={e => handleDefaultFlagChange(e.target.checked)}
              >
                <span className="text-sm">常時表示する（金額は0円固定）</span>
              </AppCheckbox>
            </AppCol>
          </AppRow>
        </AppCol>
      </AppRow>
    </AppModal>
  );
};

export default FreeMedicalTreatmentTypeEditModal;
